import { useContext, useMemo } from "react";
import {
  aggregateTextInlineStyle,
  cellInlineStylesAt,
  type TableCanvasObject,
  type TextInlineStyle,
  type TextInlineStylePatch,
} from "@refinement-canvas/domain";
import { useCanvasStore } from "../store/canvas-store";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";
import {
  useTableCellEditorStore,
  TABLE_CELL_PANEL_ATTRIBUTE,
  type TableCellEditorHandle,
} from "../input/table-cell-editor-store";
import { SelectionPanelPlacementContext } from "./selection-style-panel-frame";
import { AnchoredStylePanelFrame } from "./text-range-style-panel-frame";
import { ToolbarParentChildPanel } from "./toolbar-parent-child-panel";
import {
  renderTextRangeStyleChild,
  textRangeStyleParentItems,
  TextStyleBoldButton,
  type TextRangeStyleChildInput,
  type TextRangeStyleItemKey,
} from "./text-style-panel-model";
import { DEFAULT_TEXT_FONT_SIZE } from "../../../lib/text-size-preference";
import {
  normalizeTextColorForCompare,
  normalizeTextFillForCompare,
  TEXT_FILL_TRANSPARENT,
} from "../../../lib/text-color";
import type { CustomColorPalettes } from "./use-custom-color-palettes";

// 表のセルを編集しているあいだだけ、そのセルのそばへ出る書式パネル。
//
// - 選択が空（キャレットのみ）なら「セル用」。そのセルの本文全体へ効く。
// - 文字を選んでいれば「範囲用」。選んだ文字だけへ効く。
//   どちらも項目・順序・選択肢・現在値の集計・見た目はテキスト図形の範囲パネルと同じ共有モデルで、
//   違うのは「どの範囲へ当てるか」と見出しだけにする。
// - 適用は保存済み本文に対して行う。現在値の集計も保存済み本文と run から求めるため、
//   編集中の未送信入力は適用の直前に確定させる（prepareStyleApply）。
// - 編集中の <input> は書式を表示できないため、見た目へ現れるのは編集を抜けた後である（設計 §9.5）。

/** パネルの対象範囲。`mode` は見出し・識別子の切り替えにだけ使う。 */
interface CellStyleTarget {
  readonly mode: "cell" | "range";
  /** 現在値の集計に使う範囲（保存済み本文の長さで切り詰め済み）。 */
  readonly aggregateStart: number;
  readonly aggregateEnd: number;
  /** 適用時に渡す範囲（切り詰めは dispatcher 側が保存済み本文に対して行う）。 */
  readonly applyStart: number;
  readonly applyEnd: number;
}

function resolveTarget(
  selection: { readonly start: number; readonly end: number },
  cellTextLength: number,
  draftLength: number
): CellStyleTarget {
  const start = Math.max(0, Math.min(selection.start, cellTextLength));
  const end = Math.max(0, Math.min(selection.end, cellTextLength));
  // 他参加者が本文を短くして選択範囲が空になった場合は、セル全体を対象にするほうへ倒す
  // （押しても何も起きないパネルを出さない）。
  if (selection.start < selection.end && start < end) {
    return {
      mode: "range",
      aggregateStart: start,
      aggregateEnd: end,
      // 適用側の切り詰めは dispatcher が保存済み本文に対して行うため、ここでは元の値を渡す。
      applyStart: selection.start,
      applyEnd: selection.end,
    };
  }
  return {
    mode: "cell",
    aggregateStart: 0,
    aggregateEnd: cellTextLength,
    applyStart: 0,
    applyEnd: draftLength,
  };
}

/** セルの基底書式。表は fill と bold を図形全体としては持たないため、未設定のままにする。 */
function tableCellBaseStyle(table: TableCanvasObject): TextInlineStyle {
  return {
    ...(table.color === undefined ? {} : { color: table.color }),
    ...(table.fontSize === undefined ? {} : { fontSize: table.fontSize }),
  };
}

interface Props {
  readonly palettes: CustomColorPalettes;
}

export function TableCellStylePanel({ palettes }: Props): JSX.Element | null {
  const handle = useTableCellEditorStore((s) => s.handle);
  const selection = useTableCellEditorStore((s) => s.selection);
  const anchorRect = useTableCellEditorStore((s) => s.anchorRect);
  const draft = useTableCellEditorStore((s) => s.draft);
  const readOnly = useCanvasStore((s) => s.readOnly);
  const objects = useCanvasDocStore((s) => s.objects);
  const dispatcher = useCanvasOpDispatcherContext();

  const object = handle ? objects[handle.objectId] : undefined;
  const table = object?.type === "table" && object.locked !== true ? object : null;
  const cellText = table && handle ? (table.cells[handle.row]?.[handle.column] ?? "") : "";

  const resolved = useMemo(() => {
    if (!table || !handle || !selection) return null;
    const target = resolveTarget(selection, cellText.length, draft.length);
    const aggregate = aggregateTextInlineStyle({
      base: tableCellBaseStyle(table),
      end: target.aggregateEnd,
      runs: cellInlineStylesAt(table, handle.row, handle.column),
      start: target.aggregateStart,
      text: cellText,
    });
    return {
      target,
      values: {
        // chooser は「混在は null、それ以外は描画時と同じ実効値」を期待するため、
        // 未設定の項目は表の描画既定（文字色は CSS 既定・文字サイズは 16px）へ寄せる。
        color: aggregate.color.mixed ? null : normalizeTextColorForCompare(aggregate.color.value),
        fill: aggregate.fill.mixed ? null : normalizeTextFillForCompare(aggregate.fill.value),
        fontSize: aggregate.fontSize.mixed
          ? null
          : (aggregate.fontSize.value ?? DEFAULT_TEXT_FONT_SIZE),
        bold: aggregate.bold.mixed ? null : (aggregate.bold.value ?? false),
      },
    };
  }, [cellText, draft.length, handle, selection, table]);

  if (readOnly) return null;
  if (!handle || !table || !selection || !anchorRect || !resolved) return null;
  // 本文が空のセルでは、適用できる文字が無いためパネルを出さない（設計 §11.2）。
  if (draft.length === 0) return null;

  const { target, values } = resolved;
  const testIdPrefix = target.mode === "range" ? "table-cell-range-style" : "table-cell-style";

  const applyPatch = (patch: TextInlineStylePatch): void => {
    // 未送信の入力を確定し、そこまでの入力を履歴の 1 グループとして閉じてから当てる
    //（保存済み本文と選択範囲の基準をそろえ、取り消しの順序を入力 → 書式に保つ）。
    handle.prepareStyleApply();
    const applied = dispatcher.updateTableCellRangeStyle(
      handle.objectId,
      { row: handle.row, column: handle.column, structureRevision: handle.structureRevision },
      { start: target.applyStart, end: target.applyEnd },
      patch
    );
    // 編集中の Ctrl+Z はセル編集器が横取りするため、成立した適用だけを編集器へ知らせ、
    // 取り消しの順番へ載せる（送らなかった場合は順番も増やさない）。
    if (applied) handle.notifyStyleApplied();
  };

  const childInput: TextRangeStyleChildInput = {
    testIdPrefix,
    ariaLabelPrefix: target.mode === "range" ? "選択した文字の" : "このセルの",
    palettes,
    color: values.color,
    fill: values.fill,
    fontSize: values.fontSize,
    // 任意色の追加は行わない（ポップオーバーへフォーカスが移ると選択範囲を見失うため）。
    allowCustomColorEditing: false,
    onChooseColor: (color) => applyPatch({ color }),
    // 「背景なし」は個別ハイライトの除去（override の削除）であり、表の基底背景は残す。
    onChooseFill: (fill) => applyPatch({ fill: fill === TEXT_FILL_TRANSPARENT ? null : fill }),
    onChooseFontSize: (px) => applyPatch({ fontSize: px }),
  };

  return (
    <AnchoredStylePanelFrame
      anchorRect={anchorRect}
      testId={`${testIdPrefix}-panel`}
      markerAttribute={TABLE_CELL_PANEL_ATTRIBUTE}
    >
      <TableCellStylePanelBody
        // 対象が「セル全体」と「選択文字」で切り替わったら、開いていた子パネルは畳む
        // （見出しと適用先が変わるため、開いたままだと操作対象を取り違える）。
        key={target.mode}
        childInput={childInput}
        handle={handle}
        mode={target.mode}
        bold={values.bold}
        // 混在状態を押したときは対象範囲全体を太字へそろえる。
        onToggleBold={() => applyPatch({ bold: values.bold !== true })}
      />
    </AnchoredStylePanelFrame>
  );
}

/** 親子パネルの組み立て（配置側の情報を子の端揃えへ渡すためフレームの内側で読む）。 */
function TableCellStylePanelBody({
  childInput,
  handle,
  mode,
  bold,
  onToggleBold,
}: {
  readonly childInput: TextRangeStyleChildInput;
  readonly handle: TableCellEditorHandle;
  readonly mode: "cell" | "range";
  readonly bold: boolean | null;
  readonly onToggleBold: () => void;
}): JSX.Element {
  const parentSide = useContext(SelectionPanelPlacementContext)?.side ?? "bottom";
  const items = textRangeStyleParentItems({
    color: childInput.color,
    fill: childInput.fill,
    fontSize: childInput.fontSize,
  });
  const position = `${handle.row + 1} 行 ${handle.column + 1} 列`;
  return (
    <ToolbarParentChildPanel<TextRangeStyleItemKey>
      label={mode === "range" ? "選択した文字のスタイル" : `このセル（${position}）のスタイル`}
      items={items}
      renderChild={(key) => renderTextRangeStyleChild(key, childInput)}
      parentSide={parentSide}
      testIdPrefix={childInput.testIdPrefix}
      directActions={
        <TextStyleBoldButton
          pressed={bold}
          testId={`${childInput.testIdPrefix}-bold`}
          onToggle={onToggleBold}
        />
      }
    />
  );
}
